import { type PropsWithChildren } from 'react'
import { Box } from '../../primitives/Box'
import { type BaseProps } from '../../types'

type Props = PropsWithChildren<{
  /**
   * Semantic html for List, the inner list items by default are `li`, but they have option to output as div.
   * Use `div` only when your inner list items are not set to `li`.
   *
   * @default 'ul'
   */
  component?: 'ul' | 'ol' | 'div'

  /**
   * Space between the items
   * @default undefined (no space between List items)
   */
  gap?: number

  /**
   * @default 'column'
   */
  direction?: 'row' | 'column'
}> &
  BaseProps<HTMLUListElement | HTMLOListElement, never, { backgroundColor?: 'pure.light' | 'background.default' }>

/**
 * List
 *
 * This element should be used arround multiple ListItems. Do not use it when you
 * only use 1 ListItem, then alter its `ListComponent` prop into 'div' for example.
 *
 * Demos:
 * - [List](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/constructs-listitem--docs)
 *
 */
export const List = ({ children, component = 'ul', direction = 'column', sx, ...rest }: Props) => (
  <Box
    {...rest}
    component={component}
    sx={{
      margin: 0,
      padding: 0,
      ...sx,
      listStyle: 'none',
      display: 'flex',
      flexDirection: direction,

      // we make sure the last item of a list never has a divider
      '& > .DhlListItem:last-of-type': {
        borderBottom: 'none',
        borderRight: 'none',
      },

      // when we have list items in one single row we make sure each item is same height
      ...(direction === 'row' && { '& .DhlListItem-item': { height: '100%' } }),
    }}
  >
    {children}
  </Box>
)
